import { useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { BrandMark } from "@/components/brand/Logo";

function AuthShell({ title, subtitle, children, testId }) {
  return (
    <div className="min-h-[80vh] grid place-items-center px-4 py-12">
      <div className="w-full max-w-md kc-card p-8 sm:p-10 rise" data-testid={testId}>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#F3F1EC] mb-6"><BrandMark className="w-6 h-6" /></span>
        <h1 className="text-3xl font-semibold">{title}</h1>
        <p className="mt-2 text-[#5A606A]">{subtitle}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  );
}

const redirectFor = (user, fallback) => (user.role === "admin" ? "/admin" : fallback || "/account");

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const user = await login(form.email, form.password);
      toast.success(`Welcome back, ${user.name?.split(" ")[0] || "there"}!`);
      navigate(new URLSearchParams(location.search).get("next") || redirectFor(user, location.state?.from), { replace: true });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to manage your bookings, invoices and rewards." testId="login-page">
      <form onSubmit={submit} className="space-y-5">
        <div><label className="kc-label" htmlFor="email">Email</label>
          <input id="email" data-testid="login-email-input" type="email" required className="kc-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" /></div>
        <div><label className="kc-label" htmlFor="password">Password</label>
          <input id="password" data-testid="login-password-input" type="password" required className="kc-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} autoComplete="current-password" /></div>
        {error && <p role="alert" data-testid="login-error" className="text-sm text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-xl px-4 py-3">{error}</p>}
        <button data-testid="login-submit-button" className="btn-primary w-full" disabled={busy}>{busy ? "Logging in…" : "Log in"}</button>
      </form>
      <div className="mt-6 flex justify-between text-sm">
        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[var(--kc-blue)] font-medium hover:underline">Forgot password?</Link>
        <Link to="/signup" data-testid="go-to-signup-link" className="text-[var(--kc-blue)] font-medium hover:underline">Create account</Link>
      </div>
    </AuthShell>
  );
}

export function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: params.get("email") || "", phone: "", password: "", referral_code: params.get("ref") || "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const user = await register({ ...form, referral_code: form.referral_code || null });
      toast.success("Account created. Welcome to Kaur Cleaning!");
      navigate(params.get("next") || location.state?.from || "/account", { replace: true });
    } catch (err) { setError(errorMessage(err)); } finally { setBusy(false); }
  };

  return (
    <AuthShell title="Create your account" subtitle="Track bookings, earn rewards and rebook in one tap." testId="signup-page">
      <form onSubmit={submit} className="space-y-5">
        <div><label className="kc-label" htmlFor="name">Full name</label><input id="name" data-testid="signup-name-input" required className="kc-input" value={form.name} onChange={set("name")} /></div>
        <div><label className="kc-label" htmlFor="s-email">Email</label><input id="s-email" data-testid="signup-email-input" type="email" required className="kc-input" value={form.email} onChange={set("email")} /></div>
        <div><label className="kc-label" htmlFor="phone">Phone</label><input id="phone" data-testid="signup-phone-input" type="tel" className="kc-input" value={form.phone} onChange={set("phone")} /></div>
        <div><label className="kc-label" htmlFor="s-password">Password <span className="text-[#5A606A] font-normal">(8+ characters)</span></label>
          <input id="s-password" data-testid="signup-password-input" type="password" required minLength={8} className="kc-input" value={form.password} onChange={set("password")} autoComplete="new-password" /></div>
        <div><label className="kc-label" htmlFor="ref">Referral code <span className="text-[#5A606A] font-normal">(optional)</span></label><input id="ref" data-testid="signup-referral-input" className="kc-input uppercase" value={form.referral_code} onChange={set("referral_code")} /></div>
        {error && <p role="alert" data-testid="signup-error" className="text-sm text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-xl px-4 py-3">{error}</p>}
        <button data-testid="signup-submit-button" className="btn-primary w-full" disabled={busy}>{busy ? "Creating…" : "Create account"}</button>
      </form>
      <p className="mt-6 text-sm text-[#5A606A]">Already have an account? <Link to="/login" data-testid="go-to-login-link" className="text-[var(--kc-blue)] font-medium hover:underline">Log in</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/forgot-password", { email }); setDone(true); } catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll issue a reset link." testId="forgot-page">
      {done ? <p data-testid="forgot-success" className="text-[var(--kc-text)]">If an account exists for <strong>{email}</strong>, a reset link has been issued. Check your notifications or contact us for help.</p> : (
        <form onSubmit={submit} className="space-y-5">
          <div><label className="kc-label" htmlFor="f-email">Email</label><input id="f-email" data-testid="forgot-email-input" type="email" required className="kc-input" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          <button data-testid="forgot-submit-button" className="btn-primary w-full">Send reset link</button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="text-[var(--kc-blue)] font-medium hover:underline">Back to login</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/reset-password", { token: params.get("token"), password }); toast.success("Password updated. Please log in."); navigate("/login"); }
    catch (err) { toast.error(errorMessage(err)); }
  };
  return (
    <AuthShell title="Choose a new password" subtitle="Make it at least 8 characters." testId="reset-page">
      <form onSubmit={submit} className="space-y-5">
        <div><label className="kc-label" htmlFor="r-password">New password</label><input id="r-password" data-testid="reset-password-input" type="password" required minLength={8} className="kc-input" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
        <button data-testid="reset-submit-button" className="btn-primary w-full">Update password</button>
      </form>
    </AuthShell>
  );
}
